import { ComponentProps } from 'react';

import { useColorModeValue } from '@bonusly/design-system';

/**
 * Inline SVG for the primary Spotlight background.
 *
 * Rendered inline (not via <img src>) so it can read color-mode-aware values
 * from the app's ChakraProvider. This is important because:
 *   1. Safari (WebKit bug 199134) does not honor @media (prefers-color-scheme)
 *      inside SVGs loaded via <img>.
 *   2. The in-app color mode is user-toggleable and doesn't always match the OS.
 *
 * The background fill uses the `bg.surface` semantic token via its emitted CSS
 * variable, so it adapts automatically to any future theme changes. The two
 * shape colors that differ between light/dark are resolved via useColorModeValue.
 */
export const SpotlightBackground1 = ({ preserveAspectRatio = 'xMidYMid slice', ...rest }: ComponentProps<'svg'>) => {
	const tealFill = useColorModeValue('#72DDD3', '#A8F5EE');
	const overlayFill = useColorModeValue('#A8F5EE', '#1D1F25');
	const bgFill = 'var(--chakra-colors-bg-surface)';

	return (
		<svg
			width="100%"
			height="100%"
			viewBox="0 0 2200 1200"
			fill="none"
			preserveAspectRatio={preserveAspectRatio}
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			style={{ display: 'block' }}
			{...rest}
		>
			<defs>
				<clipPath id="spotlight-bg-1-clip-0">
					<rect width="2200" height="1200" fill="white" />
				</clipPath>
				<clipPath id="spotlight-bg-1-clip-1">
					<rect width="2201.57" height="1324" fill="white" />
				</clipPath>
				<clipPath id="spotlight-bg-1-clip-2">
					<rect width="2201.57" height="1356.11" fill="white" />
				</clipPath>
				<clipPath id="spotlight-bg-1-clip-3">
					<rect width="2273.43" height="1360.33" fill="white" transform="translate(-41.2793 -36.6934)" />
				</clipPath>
			</defs>
			<g clipPath="url(#spotlight-bg-1-clip-0)">
				<rect width="2200" height="1200" fill={bgFill} />
				<g clipPath="url(#spotlight-bg-1-clip-1)">
					<rect width="2201.57" height="1324" fill={bgFill} />
					<g clipPath="url(#spotlight-bg-1-clip-2)">
						<g clipPath="url(#spotlight-bg-1-clip-3)">
							<rect width="2273.43" height="1360.33" transform="translate(-41.2793 -36.6934)" fill={bgFill} />
							<ellipse cx="400.362" cy="783.232" rx="728.615" ry="436.051" fill="#C9ED49" />
							<path
								d="M638.205 466.682C314.078 525.792 474.109 891.176 594.64 1066.48C632.397 1104.15 795.041 1229.61 956.233 1218.6C1117.43 1207.59 1986.59 576.927 2267.55 505.799C1872.56 469.58 962.332 407.571 638.205 466.682Z"
								fill="#1E9F54"
							/>
							<path
								d="M638.205 466.682C314.078 525.792 474.109 891.176 594.64 1066.48C632.397 1104.15 795.041 1229.61 956.233 1218.6C1117.43 1207.59 1986.59 576.927 2267.55 505.799C1872.56 469.58 962.332 407.571 638.205 466.682Z"
								fill={tealFill}
							/>
							<path
								d="M338.278 1036.39C191.949 1036.39 4.39248 1204.78 -71.0947 1288.98V1606.88H2655.15V417.994C1975.77 452.833 1209.28 1132.2 1008.95 1136.55C808.622 1140.91 521.19 1036.39 338.278 1036.39Z"
								fill="#9C8EF1"
							/>
							<path
								d="M2160.74 1058.63C1785.8 1058.63 1764 1384.99 1689.88 1554.71H2845.23V775.777C2699.9 874.412 2535.68 1058.63 2160.74 1058.63Z"
								fill="#7B5CE0"
							/>
							<path d="M864.365 1454.08C1509.12 880.131 213.394 937.277 -59.9141 861.496V1454.08H864.365Z" fill="#4BB475" />
							<g style={{ mixBlendMode: 'overlay' }}>
								<ellipse cx="642.613" cy="639.746" rx="242.25" ry="221.752" fill={overlayFill} />
							</g>
						</g>
					</g>
				</g>
			</g>
		</svg>
	);
};
